/**
 * Histórico de versões de um manual (só quem edita): o que mudou de uma
 * versão para a anterior, passo a passo, e "Restaurar vN" — que grava a
 * versão seguinte com o conteúdo antigo, sem apagar nada (ADR-0087).
 */
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import ConfirmDialog from "../../components/ConfirmDialog";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { lerVersao, listarVersoes, restaurarVersao } from "../../services/areaPcp";
import { dataHoraLocal } from "./dominio/formatos";
import { compararVersoes, totalDeMudancas } from "./dominio/versoes";
import { Aviso, BotaoSecundario, Meta, Selo } from "./styles";

const Versao = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ $selecionada }) => ($selecionada ? "var(--accent)" : "var(--border)")};
  background: ${({ $selecionada }) => ($selecionada ? "var(--accent-soft)" : "var(--surface)")};
  border-radius: 10px;
  text-align: left;
  font: inherit;
  color: var(--ink);
  cursor: pointer;

  strong {
    font-size: 13.5px;
  }
`;

const Mudanca = styled.div`
  border-top: 1px solid var(--border);
  padding: 10px 0;
  font-size: 13px;

  .titulo {
    font-weight: 700;
    margin-bottom: 4px;
  }
  .antes {
    color: var(--danger-text);
    text-decoration: line-through;
  }
  .depois {
    color: var(--success-text);
  }
`;

const TIPO_DA_MUDANCA = { novo: ["success", "novo"], removido: ["danger", "removido"], alterado: ["info", "alterado"] };

function Comparacao({ comparacao }) {
  if (!comparacao) return null;
  if (!totalDeMudancas(comparacao)) return <Meta>Sem mudanças de conteúdo.</Meta>;
  return (
    <div>
      {comparacao.campos.map((c) => (
        <Mudanca key={c.campo}>
          <div className="titulo">{c.rotulo}</div>
          {c.antes ? <div className="antes">− {c.antes}</div> : null}
          {c.depois ? <div className="depois">+ {c.depois}</div> : null}
        </Mudanca>
      ))}
      {comparacao.reordenado ? <Mudanca><div className="titulo">Passos reordenados</div></Mudanca> : null}
      {comparacao.secoes ? <Mudanca><div className="titulo">Seções alteradas</div></Mudanca> : null}
      {comparacao.passos.map((p) => (
        <Mudanca key={`${p.tipo}-${p.numero}-${p.titulo}`}>
          <div className="titulo">
            Passo {p.numero} · {p.titulo} <Selo $tom={TIPO_DA_MUDANCA[p.tipo][0]}>{TIPO_DA_MUDANCA[p.tipo][1]}</Selo>
          </div>
          {p.campos.map((c) => (
            <div key={c.campo}>
              <Meta>{c.rotulo}</Meta>
              {c.campo === "referencia" ? <div>referência alterada</div> : (
                <>
                  {c.antes ? <div className="antes">− {c.antes}</div> : null}
                  {c.depois ? <div className="depois">+ {c.depois}</div> : null}
                </>
              )}
            </div>
          ))}
        </Mudanca>
      ))}
    </div>
  );
}

export default function VersoesPainel({ tarefa, aberto, onFechar, onRestaurada }) {
  const { request } = useAuth();
  const toast = useToast();
  const [versoes, setVersoes] = useState([]);
  const [selecionada, setSelecionada] = useState(null);
  const [comparacao, setComparacao] = useState(null);
  const [aRestaurar, setARestaurar] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!aberto) return;
    setErro("");
    setComparacao(null);
    setSelecionada(null);
    listarVersoes(request, tarefa.id).then((r) => setVersoes(r.versoes)).catch((f) => setErro(f.message));
  }, [aberto, request, tarefa.id]);

  useEffect(() => {
    if (!selecionada) return;
    let ativo = true;
    const anterior = selecionada.numero > 1 ? lerVersao(request, tarefa.id, selecionada.numero - 1) : Promise.resolve(null);
    Promise.all([anterior, lerVersao(request, tarefa.id, selecionada.numero)])
      .then(([a, b]) => {
        if (ativo) setComparacao(compararVersoes(a?.conteudo || null, b.conteudo));
      })
      .catch((f) => setErro(f.message));
    return () => {
      ativo = false;
    };
  }, [selecionada, request, tarefa.id]);

  const restaurar = async () => {
    try {
      const restaurada = await restaurarVersao(request, tarefa.id, aRestaurar.numero, tarefa.versao_atual);
      toast.success(`Versão ${aRestaurar.numero} restaurada como versão ${restaurada.versao_atual}`);
      setARestaurar(null);
      onRestaurada(restaurada);
    } catch (falha) {
      setARestaurar(null);
      setErro(falha.message);
    }
  };

  return (
    <SlideOverPanel open={aberto} onClose={onFechar} title={`Versões · ${tarefa.titulo}`} width={560}>
      {erro ? <Aviso $tom="danger">{erro}</Aviso> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {versoes.map((v) => (
          <Versao
            key={v.numero}
            type="button"
            $selecionada={selecionada?.numero === v.numero}
            onClick={() => setSelecionada(v)}
          >
            <strong>
              v{v.numero} {v.numero === tarefa.versao_atual ? <Selo $tom="info">atual</Selo> : null}
            </strong>
            <Meta>
              {v.criada_por || "Manual inicial"} · {dataHoraLocal(v.criada_em)} · {v.resumo}
            </Meta>
          </Versao>
        ))}
      </div>
      {selecionada ? (
        <>
          <h3 style={{ margin: "16px 0 4px", fontSize: 14 }}>
            {selecionada.numero > 1 ? `v${selecionada.numero - 1} → v${selecionada.numero}` : `v${selecionada.numero}`}
          </h3>
          <Comparacao comparacao={comparacao} />
          {selecionada.numero !== tarefa.versao_atual ? (
            <BotaoSecundario type="button" onClick={() => setARestaurar(selecionada)} style={{ marginTop: 8 }}>
              Restaurar v{selecionada.numero}
            </BotaoSecundario>
          ) : null}
        </>
      ) : null}
      <ConfirmDialog
        open={Boolean(aRestaurar)}
        titulo={`Restaurar a versão ${aRestaurar?.numero}?`}
        descricao={`O conteúdo da v${aRestaurar?.numero} vira a versão ${tarefa.versao_atual + 1}. A versão atual continua no histórico. Execuções abertas seguem na versão em que começaram.`}
        confirmarLabel="Restaurar"
        tone="info"
        onConfirmar={restaurar}
        onCancelar={() => setARestaurar(null)}
      />
    </SlideOverPanel>
  );
}
